import { useDataSourceColumns, useTranslation } from "@/hooks";
import { executeEnhancement } from "@/services/enhance/enhancementExecutor";
import { getEnhancementService } from "@/services/enhance/enhancementRegistry";
import {
  useDataProcessingStore,
  useProcessedData,
} from "@/stores/data-processing";
import { useActiveDataflowId } from "@/stores/dataflow";
import type {
  EnhancementExecutionOptions,
  EnhancementMapping,
} from "@/types/enhance";
import {
  Alert,
  Button,
  Flex,
  Modal,
  Select,
  Stack,
  TagsInput,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { WorkspaceModalEnhancementLoading } from "./WorkspaceModalEnhancementLoading";
import {
  EnhancementResultsModal,
  type EnhancementResultsData,
} from "./WorkspaceModalEnhancementResults";
import { WorkspaceModalEnhancementUndefined } from "./WorkspaceModalEnhancementUndefined";

export function WorkspaceModalEnhancement({
  onClose,
  title,
  serviceId,
}: {
  onClose: () => void;
  title?: string;
  serviceId: string;
}) {
  const { t } = useTranslation();
  const activeDataflowId = useActiveDataflowId();
  const processedData = useProcessedData(activeDataflowId || "");
  const availableColumns = useDataSourceColumns();
  const updateProcessedData = useDataProcessingStore(
    (state) => state.updateProcessedData
  );
  const modalTitle = title || t("enhancement").enhanceData;

  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [resultsModalOpened, setResultsModalOpened] = useState(false);
  const [resultsData, setResultsData] = useState<EnhancementResultsData | null>(
    null
  );
  const [loadingProgress, setLoadingProgress] = useState({
    total: 0,
    completed: 0,
    currentBatch: 0,
    totalBatches: 0,
  });
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const selectedService = getEnhancementService(serviceId);

  const form = useForm<{
    requestColumns: string[];
    responseFields: string[];
    matchMode: "best_guess" | "exact_match";
    serviceOptions?: Record<string, any>;
  }>({
    initialValues: {
      requestColumns: [],
      responseFields: selectedService?.defaultResponseFields || [],
      matchMode: "best_guess",
      serviceOptions: {},
    },
    validate: {
      requestColumns: (value) => {
        return !value || value.length === 0
          ? t("enhancement").atLeastOneInputColumnRequired
          : null;
      },
      responseFields: (value) =>
        !value || value.length === 0
          ? t("enhancement").atLeastOneResponseFieldRequired
          : null,
    },
  });

  const responseFieldOptions = useMemo(() => {
    if (!selectedService) return [];
    return selectedService.responseFields.map((field) => field.id);
  }, [serviceId]);

  // Transform available columns to include first row values in dropdown
  const columnOptionsWithValues = useMemo(() => {
    if (!processedData?.data.length || !availableColumns.length) {
      return availableColumns.map((col) => ({ value: col, label: col }));
    }
    const firstRow = processedData.data[0];
    return availableColumns.map((col) => {
      const value = firstRow[col];
      const displayValue =
        value !== undefined && value !== null && value !== ""
          ? String(value).trim()
          : "";
      return {
        value: col,
        label: col,
        firstRowValue: displayValue,
      };
    });
  }, [availableColumns, processedData]);

  useEffect(() => {
    if (!selectedService) return;

    form.setFieldValue("requestColumns", []);
    form.setFieldValue("matchMode", "best_guess");

    if (selectedService.serviceOptions) {
      const defaultOptions: Record<string, any> = {};
      selectedService.serviceOptions.forEach((opt) => {
        if (opt.defaultValue !== undefined) {
          defaultOptions[opt.id] = opt.defaultValue;
        }
      });
      form.setFieldValue("serviceOptions", defaultOptions);
    } else {
      form.setFieldValue("serviceOptions", {});
    }

    // Set default response fields if defined, otherwise empty array
    const defaultResponseFields = selectedService.defaultResponseFields || [];
    form.setFieldValue("responseFields", defaultResponseFields);
  }, [serviceId]);

  const exampleValues = useMemo(() => {
    if (!processedData?.data.length) return null;

    const firstRow = processedData.data[0];
    const requestColumns = form.values.requestColumns;

    if (requestColumns.length === 0) return null;
    const values = requestColumns
      .map((col) => {
        // If it's a custom value (not a column name), return it as-is (preserve user's spacing)
        if (!availableColumns.includes(col)) {
          return col || null;
        }
        // Otherwise, get the value from the row data and trim it
        const value = firstRow[col];
        return value !== undefined && value !== null && value !== ""
          ? String(value).trim()
          : null;
      })
      .filter((v): v is string => v !== null && v !== "");
    return values.length > 0 ? values.join(" ") : null;
  }, [processedData, form.values.requestColumns, availableColumns]);

  const handleEnhance = async (values: {
    requestColumns: string[];
    responseFields: string[];
    matchMode: "best_guess" | "exact_match";
    serviceOptions?: Record<string, any>;
  }) => {
    if (!activeDataflowId || !processedData || !processedData.data.length) {
      setProcessingError(t("enhancement").noDataAvailableToEnhance);
      return;
    }

    if (!selectedService) {
      setProcessingError(t("enhancement").pleaseSelectEnhancementService);
      return;
    }

    setIsProcessing(true);
    setProcessingError(null);
    const startTime = Date.now();
    setProcessingStartTime(startTime);

    // Create new AbortController for this execution
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const data = [...processedData.data];

      const mapping: EnhancementMapping = {
        serviceId: serviceId,
        requestColumns: values.requestColumns,
        responseFields: values.responseFields,
        matchMode: values.matchMode,
      };

      const batchSize = selectedService.maxBatchSize || 5000;
      const totalBatches = Math.ceil(data.length / batchSize);

      setLoadingProgress({
        total: data.length,
        completed: 0,
        currentBatch: 0,
        totalBatches,
      });

      const executionOptions: EnhancementExecutionOptions = {
        mapping: {
          ...mapping,
          serviceOptions: form.values.serviceOptions,
        },
        serviceOptions: form.values.serviceOptions,
        abortSignal: abortController.signal,
        onProgress: (_rowIndex, current, total) => {
          const completedBatches = Math.floor(current / batchSize);
          const currentBatchNum =
            completedBatches < totalBatches
              ? completedBatches + 1
              : totalBatches;

          setLoadingProgress({
            total,
            completed: current,
            currentBatch: currentBatchNum,
            totalBatches,
          });
        },
      };

      const results = await executeEnhancement(
        selectedService,
        data,
        mapping,
        executionOptions
      );

      // Check if cancelled - if so, don't store results
      if (abortController.signal.aborted) {
        setIsProcessing(false);
        setProcessingStartTime(null);
        abortControllerRef.current = null;
        return;
      }

      const existingColumns = processedData.metadata.columns || [];
      const newColumns: string[] = [...existingColumns];

      for (const fieldId of values.responseFields) {
        const field = selectedService.responseFields.find(
          (f) => f.id === fieldId
        );
        if (field && !newColumns.includes(fieldId)) {
          newColumns.push(fieldId);
        }
      }

      for (const result of results) {
        if (result.rowIndex >= 0 && result.rowIndex < data.length) {
          if (result.success && result.fields) {
            for (const [fieldId, value] of Object.entries(result.fields)) {
              if (value !== null && value !== undefined) {
                data[result.rowIndex][fieldId] = value;
              }
            }
          }
        }
      }

      await updateProcessedData(activeDataflowId, data, newColumns);

      const successCount = results.filter((r) => r.success).length;
      const failedCount = results.length - successCount;
      const durationMs = Date.now() - startTime;

      const resultsDataForModal: EnhancementResultsData = {
        serviceName: selectedService.name,
        totalCount: results.length,
        successCount,
        failedCount,
        durationMs,
        results: results.map((result, idx) => ({
          rowIndex: idx,
          inputData: data[idx],
          outputFields: result.fields,
          error: result.error,
          success: result.success,
        })),
      };

      setResultsData(resultsDataForModal);
      setResultsModalOpened(true);
      setIsProcessing(false);
      setProcessingStartTime(null);
      abortControllerRef.current = null;
    } catch (error) {
      // Check if error is due to cancellation
      if (error instanceof Error && error.name === "AbortError") {
        setIsProcessing(false);
        setProcessingStartTime(null);
        abortControllerRef.current = null;
        return;
      }

      setProcessingError(
        error instanceof Error
          ? error.message
          : t("enhancement").unknownErrorOccurred
      );
      setIsProcessing(false);
      setProcessingStartTime(null);
      abortControllerRef.current = null;
    }
  };

  if (!processedData || !processedData.data.length) {
    return (
      <Modal
        opened={true}
        onClose={onClose}
        title={modalTitle}
        centered
        size="md"
      >
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          <Text size="sm">{t("enhancement").noProcessedDataAvailable}</Text>
        </Alert>
        <Flex justify="flex-end" gap="sm" mt="md">
          <Button variant="outline" onClick={onClose}>
            {t("common").close}
          </Button>
        </Flex>
      </Modal>
    );
  }

  if (!selectedService) {
    return (
      <WorkspaceModalEnhancementUndefined
        opened={true}
        onClose={onClose}
        serviceId={serviceId}
      />
    );
  }

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsProcessing(false);
    setProcessingStartTime(null);
  };

  return (
    <>
      {isProcessing && (
        <WorkspaceModalEnhancementLoading
          total={loadingProgress.total}
          completed={loadingProgress.completed}
          onCancel={handleCancel}
          serviceName={selectedService?.name}
          startTime={processingStartTime || undefined}
        />
      )}

      <Modal
        opened={true && !isProcessing}
        onClose={onClose}
        title={modalTitle}
        centered
        size="lg"
        closeOnClickOutside={!isProcessing}
        closeOnEscape={!isProcessing}
      >
        <form onSubmit={form.onSubmit(handleEnhance)}>
          <Stack gap="md">
            {processingError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {processingError}
              </Alert>
            )}

            {selectedService && (
              <>
                {selectedService.serviceOptions &&
                  selectedService.serviceOptions.length > 0 && (
                    <Stack gap="sm">
                      {selectedService.serviceOptions.map((option) => {
                        if (option.type === "select" && option.options) {
                          return (
                            <Select
                              key={option.id}
                              label={option.label}
                              description={option.description}
                              data={option.options.map((opt) => ({
                                value: opt.value,
                                label: opt.label,
                              }))}
                              value={
                                form.values.serviceOptions?.[option.id] ||
                                option.defaultValue ||
                                ""
                              }
                              onChange={(value) => {
                                form.setFieldValue("serviceOptions", {
                                  ...form.values.serviceOptions,
                                  [option.id]: value || option.defaultValue,
                                });
                              }}
                              disabled={isProcessing}
                              required={option.required}
                            />
                          );
                        }
                        return null;
                      })}
                    </Stack>
                  )}

                <Alert color="blue">
                  <Text size="sm" c={exampleValues ? undefined : "gray.9"}>
                    {exampleValues || "Select columns to see example"}
                  </Text>
                </Alert>

                <TagsInput
                  style={{ flex: 1 }}
                  label={t("enhancement").inputColumns}
                  description={t("enhancement").selectDataColumnsDescription}
                  placeholder={t("enhancement").selectColumns}
                  data={columnOptionsWithValues}
                  disabled={isProcessing}
                  value={form.values.requestColumns}
                  onChange={(value) => {
                    form.setFieldValue("requestColumns", value || []);
                  }}
                  renderOption={({ option }) => {
                    const opt = option as {
                      value: string;
                      label: string;
                      firstRowValue?: string;
                    };
                    return (
                      <Flex
                        justify="space-between"
                        align="center"
                        w="100%"
                        gap="sm"
                      >
                        <Text size="sm" flex={1}>
                          {opt.label}
                        </Text>
                        {opt.firstRowValue && (
                          <Text truncate size="sm" c="gray.9" flex={1}>
                            {opt.firstRowValue}
                          </Text>
                        )}
                      </Flex>
                    );
                  }}
                />
                <Select
                  style={{ flex: 1 }}
                  label="Match Mode"
                  description="How to match and return results"
                  data={[
                    {
                      value: "best_guess",
                      label:
                        "Best Match - Returns the best available match even if confidence is lower",
                    },
                    {
                      value: "exact_match",
                      label:
                        "Exact Match - Only returns results with high confidence (≥80%) and exact match type",
                    },
                  ]}
                  value={form.values.matchMode}
                  onChange={(value) => {
                    form.setFieldValue(
                      "matchMode",
                      (value as "best_guess" | "exact_match") || "best_guess"
                    );
                  }}
                  disabled={isProcessing}
                />

                <TagsInput
                  label={t("enhancement").responseFields}
                  description={t("enhancement").chooseFieldsDescription}
                  placeholder={t("enhancement").selectResponseFields}
                  data={responseFieldOptions}
                  disabled={isProcessing}
                  value={form.values.responseFields || []}
                  onChange={(value) => {
                    const validFieldIds = selectedService.responseFields.map(
                      (f) => f.id
                    );
                    const filteredValue = value.filter((id) =>
                      validFieldIds.includes(id)
                    );
                    form.setFieldValue("responseFields", filteredValue || []);
                  }}
                />
              </>
            )}

            <Flex justify="space-between" gap="sm" mt="md">
              <Button
                variant="subtle"
                onClick={onClose}
                c="gray.9"
                disabled={isProcessing}
              >
                {t("common").cancel}
              </Button>
              <Button
                type="submit"
                w={200}
                loading={isProcessing}
                disabled={
                  !form.isValid() ||
                  !selectedService ||
                  form.values.requestColumns.length === 0 ||
                  !form.values.responseFields.length
                }
              >
                {t("enhancement").enhance}
              </Button>
            </Flex>
          </Stack>
        </form>
      </Modal>

      {resultsData && (
        <EnhancementResultsModal
          opened={resultsModalOpened}
          onClose={() => {
            setResultsModalOpened(false);
            onClose();
          }}
          data={resultsData}
        />
      )}
    </>
  );
}
